import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { devLog } from '../services/devLog'
import {
  listComments,
  createComment,
  updateComment,
  deleteComment,
  resolveComment,
  batchClassifyComments,
  batchResolveComments,
  searchComments,
} from '../services/commentsApi'
import type { PaginatedComments } from '../services/commentsApi'
import type { NegotiationComment } from '../services/negotiationsApi'

const COMMENTS_PAGE_SIZE = 50

/** Fetch comments for a negotiation with pagination (load more pattern) */
export function useCommentList(negotiationId: number | undefined, version?: number) {
  const [loadedComments, setLoadedComments] = useState<NegotiationComment[]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  const query = useQuery<PaginatedComments>({
    queryKey: ['comments', negotiationId, version, 0],
    queryFn: async () => {
      const res = await listComments(negotiationId!, version, {
        threaded: true,
        limit: COMMENTS_PAGE_SIZE,
        offset: 0,
      })
      const paginated = res as PaginatedComments
      setLoadedComments(paginated.results)
      setOffset(paginated.results.length)
      setHasMore(paginated.has_more)
      setTotalCount(paginated.total_count)
      return paginated
    },
    enabled: negotiationId != null,
    staleTime: 10_000,
  })

  const loadMore = useCallback(async () => {
    if (!negotiationId || !hasMore) return
    try {
      const res = await listComments(negotiationId, version, {
        threaded: true,
        limit: COMMENTS_PAGE_SIZE,
        offset,
      })
      const paginated = res as PaginatedComments
      setLoadedComments(prev => [...prev, ...paginated.results])
      setOffset(prev => prev + paginated.results.length)
      setHasMore(paginated.has_more)
    } catch {
      devLog.warn('Comments', 'Failed to load more comments')
    }
  }, [negotiationId, version, offset, hasMore])

  return {
    ...query,
    data: loadedComments.length > 0 ? loadedComments : (query.data?.results ?? []),
    hasMore,
    totalCount,
    loadMore,
  }
}

/** Create a new comment */
export function useCreateComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      negotiationId: number
      content: string
      visibility?: 'INTERNAL' | 'ALL' | 'SPECIFIC'
      target_tag?: string
      parent_comment_id?: number
      anchor_text?: string
      anchor_paragraph?: number
      word_cc_id?: string
      para_id?: string
    }) => {
      devLog.info('Comment', `Creating comment on negotiation ${params.negotiationId}`)

      const comment = await createComment(params.negotiationId, {
        content: params.content,
        visibility: params.visibility,
        target_tag: params.target_tag,
        parent_comment_id: params.parent_comment_id,
        anchor_text: params.anchor_text,
        anchor_paragraph: params.anchor_paragraph,
        word_cc_id: params.word_cc_id,
        para_id: params.para_id,
      })

      devLog.info('Comment', `Created comment id=${comment.id}, tag=${comment.word_cc_tag}`)
      return comment
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['comments', params.negotiationId] })
    },
  })
}

/** Update a comment's content or visibility */
export function useUpdateComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      negotiationId: number
      commentId: number
      content?: string
      visibility?: 'INTERNAL' | 'ALL' | 'SPECIFIC'
      target_tag?: string
    }) => {
      return await updateComment(params.negotiationId, params.commentId, {
        content: params.content,
        visibility: params.visibility,
        target_tag: params.target_tag,
      })
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['comments', params.negotiationId] })
    },
  })
}

/** Soft-delete a comment */
export function useDeleteComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number; commentId: number }) => {
      devLog.info('Comment', `Deleting comment ${params.commentId}`)
      await deleteComment(params.negotiationId, params.commentId)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['comments', params.negotiationId] })
    },
  })
}

/** Resolve or unresolve a comment */
export function useResolveComment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { negotiationId: number; commentId: number }) => {
      devLog.info('Comment', `Resolving comment ${params.commentId}`)
      return await resolveComment(params.negotiationId, params.commentId)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['comments', params.negotiationId] })
    },
  })
}

/** Batch-classify comments with unknown #-tags */
export function useBatchClassify() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      negotiationId: number
      commentIds: number[]
      visibility: 'INTERNAL' | 'ALL' | 'SPECIFIC'
      targetTag?: string
    }) => {
      devLog.info('Comment', `Batch-classifying ${params.commentIds.length} comments`)
      return await batchClassifyComments(params.negotiationId, {
        comment_ids: params.commentIds,
        visibility: params.visibility,
        target_tag: params.targetTag,
      })
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['comments', params.negotiationId] })
    },
  })
}

/** Batch-resolve comments (e.g. "resolve all mine") */
export function useBatchResolveComments() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      negotiationId: number
      data: { comment_ids?: number[]; scope?: 'all_mine' | 'thread'; parent_id?: number }
    }) => {
      devLog.info('Comment', `Batch-resolving comments for negotiation ${params.negotiationId}`)
      return await batchResolveComments(params.negotiationId, params.data)
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['comments', params.negotiationId] })
    },
  })
}

/** Search comments (server-side decrypt-then-filter) */
export function useCommentSearch(
  negotiationId: number | undefined,
  query: string,
  version?: number,
) {
  return useQuery<NegotiationComment[]>({
    queryKey: ['comments-search', negotiationId, query, version],
    queryFn: () => searchComments(negotiationId!, query, version),
    enabled: negotiationId != null && query.trim().length >= 2,
    staleTime: 5_000,
  })
}
